import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal, DetailModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { Buttons, HeaderButton } from "components";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { FilterAltOffRounded } from "@mui/icons-material";
import Filters from "components/Filters";
import { ACTIONS } from "consts/Actions";
import { habitacionesTitles as titles } from "consts/titulos-de-tablas";
import styles from "styles/pages/Pages.module.scss";
import { useService } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { SFHabitacionGet } from "services/services-mia/SFHabitacionGet";
import { SFUsuarioHotelGet } from "services/services-mia/SFUsuarioHotelGet";
import {
    GENERO_FILTER_ALL,
    GENERO_FILTER_OPTIONS,
    GENERO_LABELS,
    STATUS_FILTER_ALL,
    STATUS_FILTER_OPTIONS,
    STATUS_LABELS,
} from "consts/hotel";
import { formatUltimoEstado } from "../utils/mapHotelUsuario";

export default function HabitacionesHotel() {
    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [loading, setLoading] = useState(false);
    const [habitaciones, setHabitaciones] = useState([]);
    const [hoteles, setHoteles] = useState([]);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const { service } = useService();

    const updateList = useCallback(async (values) => {
        setRowSelected({ status: false, row: {} });
        setHabitaciones([]);

        const hotelId = values?.hotelId;
        if (!hotelId) return;

        setLoading(true);
        const result = await SFHabitacionGet(credentials, {
            SFHotelId: Number(hotelId),
            SFHabitacionStatus: values?.estado === STATUS_FILTER_ALL ? null : values?.estado,
            SFHabitacionGenero: values?.genero === GENERO_FILTER_ALL ? null : values?.genero,
        });

        const newRows = result?.[0]?.SFHabitacionId
            ? result.map((item) => ({
                id: item.SFHabitacionId,
                hotelId: item.SFHotelId,
                numero: item.SFHabitacionNumero,
                piso: item.SFHabitacionPiso,
                plazas: item.SFHabitacionPlazas,
                plazasOcupadas: item.plazasOcupadas,
                plazasReservadas: item.plazasReservadas,
                plazasLibres: item.plazasLibres,
                genero: GENERO_LABELS[item.SFHabitacionGenero] ?? item.SFHabitacionGenero,
                status: STATUS_LABELS[item.SFHabitacionStatus] ?? item.SFHabitacionStatus,
                fechaStatus: formatUltimoEstado(item.ultimoCambioStatusUFH),
                version: item.SFHabitacionVersion,
            }))
            : [];
        setHabitaciones(newRows);
        setLoading(false);
    }, [credentials]);

    const formik = useFormik({
        initialValues: {
            hotelId: "",
            estado: STATUS_FILTER_ALL,
            genero: GENERO_FILTER_ALL,
        },
        onSubmit: () => {
            updateList(formik.values);
        },
    });

    const resetFilters = () => {
        const defaultHotelId = hoteles[0]?.SFHotelId
            ? String(hoteles[0].SFHotelId)
            : "";
        formik.resetForm({
            values: {
                hotelId: defaultHotelId,
                estado: STATUS_FILTER_ALL,
                genero: GENERO_FILTER_ALL,
            },
        });
        updateList({ hotelId: defaultHotelId, estado: STATUS_FILTER_ALL });
    };

    const loadHoteles = useCallback(async () => {
        const result = await SFUsuarioHotelGet(credentials);
        if (Array.isArray(result) && result[0]?.SFHotelId) {
            setHoteles(result);
            const firstHotelId = String(result[0].SFHotelId);
            if (!formik.values.hotelId) {
                formik.setFieldValue("hotelId", firstHotelId);
                updateList({ hotelId: firstHotelId, estado: STATUS_FILTER_ALL });
            }
        }
    }, [credentials, formik, updateList]);

    const closeModal = () => setModalDetails({ isOpen: false });

    const handleClick = ({ procede, title }) => {
        setModalDetails({ procede, title, isOpen: true });
    };

    useEffect(() => {
        loadHoteles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setRoute({
            wordKey: `${service?.servicioNombre || "Hoteles"} - Habitaciones`,
        });
    }, [service?.servicioNombre, setRoute]);

    return (
        <main className={styles.main}>
            <Modal
                compact={modalDetails.procede !== ACTIONS.DETAIL}
                wide={modalDetails.procede === ACTIONS.DETAIL}
                open={modalDetails.isOpen}
                title={modalDetails.title}
                onClose={closeModal}
            >
                {modalDetails.procede === ACTIONS.DETAIL && (
                    <DetailModal
                        habitacion={rowSelected.row}
                        closeModal={closeModal}
                        onUpdate={() => updateList(formik.values)}
                    />
                )}
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        user={rowSelected.row}
                        updateList={() => updateList(formik.values)}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.EXPORT && (
                    <DeleteModal
                        user={rowSelected.row}
                        updateList={() => updateList(formik.values)}
                        closeModal={closeModal}
                        setterRowSelected={setRowSelected}
                    />
                )}
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        defaultHotelId={formik.values.hotelId}
                        updateList={() => updateList(formik.values)}
                        closeModal={closeModal}
                    />
                )}
            </Modal>

            <div className={styles["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "select",
                                name: "hotelId",
                                label: "Hotel",
                                disabled: false,
                                options: hoteles,
                                firstOptionKey: "SFHotelId",
                                firstOptionName: "SFHotelNombre",
                                firstOptionValue: "SFHotelId",
                                all: false,
                                width: 260,
                            },
                            {
                                type: "select",
                                name: "estado",
                                label: "Estado",
                                disabled: false,
                                options: STATUS_FILTER_OPTIONS,
                                firstOptionKey: "value",
                                firstOptionName: "label",
                                firstOptionValue: "value",
                                all: false,
                            },
                            {
                                type: "select",
                                name: "genero",
                                label: "Género",
                                disabled: false,
                                options: GENERO_FILTER_OPTIONS,
                                firstOptionKey: "value",
                                firstOptionName: "label",
                                firstOptionValue: "value",
                                all: false,
                            },
                        ]}
                        disabled={!formik.values.hotelId || rowSelected.row.id}
                    />
                    <HeaderButton text="Limpiar filtros" onClick={resetFilters}>
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text="Actualizar"
                        onClick={() => updateList(formik.values)}
                        disabled={!formik.values.hotelId || rowSelected.row.id}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text="Nueva habitación"
                        handleClick={handleClick}
                        data={{ title: "Nueva habitación", procede: ACTIONS.INSERT }}
                        disabled={
                            !formik.values.hotelId ||
                            rowSelected.row.id ||
                            service?.servicioPermiso !== "E"
                        }
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text="Ver huéspedes"
                        handleClick={handleClick}
                        data={{
                            title: `Huéspedes - Hab. ${rowSelected.row?.numero ?? ""}`,
                            procede: ACTIONS.DETAIL,
                        }}
                        disabled={!rowSelected.row.id}
                    >
                        <VisibilityOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text="Modificar habitación"
                        handleClick={handleClick}
                        data={{ title: "Modificar habitación", procede: ACTIONS.UPDATE }}
                        disabled={!rowSelected.row.id}
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text="Eliminar habitación"
                        handleClick={handleClick}
                        data={{ title: "Eliminar habitación", procede: ACTIONS.EXPORT }}
                        disabled={!rowSelected.row.id}
                    >
                        <DeleteOutlineRoundedIcon />
                    </HeaderButton>
                </Buttons>
            </div>

            <div className={styles.container}>
                <XGrid
                    rows={habitaciones}
                    titles={titles}
                    loading={loading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
